import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Scale, TrendingUp, TrendingDown, Minus, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../ui/Card';
import { ExpectedChart } from './tmCharts';
import { SectionTitle, useFmt } from './tmShared';
import type { Expected, ExpectedVerdict } from '../../services/trafficManagerService';

/** Selo do veredito: ícone + texto (nunca só cor). */
export const VerdictPill: React.FC<{ v: ExpectedVerdict; indice?: number | null }> = ({ v, indice }) => {
  const { t } = useTranslation();
  const map: Record<ExpectedVerdict, { icon: React.ElementType; cls: string; label: string }> = {
    acima: { icon: TrendingUp, cls: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300', label: t('trafficManager.expected.above', 'acima da média') },
    abaixo: { icon: TrendingDown, cls: 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300', label: t('trafficManager.expected.below', 'abaixo da média') },
    na_media: { icon: Minus, cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200', label: t('trafficManager.expected.onAverage', 'na média') },
    pouco_volume: { icon: HelpCircle, cls: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400', label: t('trafficManager.expected.lowVolume', 'pouco volume') },
    sem_base: { icon: HelpCircle, cls: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400', label: t('trafficManager.expected.noBase', 'sem média') }
  };
  const x = map[v] || map.sem_base;
  const Icon = x.icon;
  const pct = indice != null && (v === 'acima' || v === 'abaixo' || v === 'na_media') ? ` ${indice >= 1 ? '+' : '−'}${Math.abs(Math.round((indice - 1) * 100))}%` : '';
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${x.cls}`}><Icon className="w-3.5 h-3.5" />{x.label}{pct}</span>;
};

/** Variação de uma taxa contra a média, com o lado bom explícito. */
const Comparado: React.FC<{ label: string; real: number | null; base: number | null; fmt: (v: number | null) => string; lowerIsBetter?: boolean }> = ({ label, real, base, fmt, lowerIsBetter }) => {
  const { t } = useTranslation();
  const pct = real != null && base ? ((real - base) / base) * 100 : null;
  const bom = pct != null && (lowerIsBetter ? pct < 0 : pct > 0);
  return (
    <div className="min-w-0">
      <div className="text-xs text-gray-500 truncate">{label}</div>
      <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">{fmt(real)}</div>
      <div className="text-xs text-gray-500 tabular-nums">
        {t('trafficManager.expected.avgShort', 'média {{v}}', { v: fmt(base) })}
        {pct != null && Math.abs(pct) >= 1 && <span className={`ml-1 font-medium ${bom ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>{pct > 0 ? '+' : '−'}{Math.abs(pct).toFixed(0)}%<span className="sr-only">{bom ? t('trafficManager.overview.better', 'melhor') : t('trafficManager.overview.worse', 'pior')}</span></span>}
      </div>
    </div>
  );
};

/**
 * Esperado x real: o mês contra a média da própria conta (mesmas campanhas,
 * 90 dias antes do mês). "Acima/abaixo" só quando a diferença passa do acaso.
 */
const ExpectedCard: React.FC<{ e: Expected; cur: string }> = ({ e, cur }) => {
  const { t } = useTranslation();
  const f = useFmt();
  const [todas, setTodas] = useState(false);
  const money = (v: number | null) => (v == null ? '—' : f.money(v, cur));
  const pct = (v: number | null) => (v == null ? '—' : `${f.dec(v, 2)}%`);
  const camp = e.por_campanha.filter((c) => c.esperado != null || c.conversoes > 0);
  const visiveis = todas ? camp : camp.slice(0, 6);
  const titulo = e.indice != null && (e.veredito === 'acima' || e.veredito === 'abaixo')
    ? t(e.veredito === 'acima' ? 'trafficManager.expected.headAbove' : 'trafficManager.expected.headBelow', e.veredito === 'acima' ? '{{p}}% acima da média' : '{{p}}% abaixo da média', { p: Math.abs(Math.round((e.indice - 1) * 100)) })
    : e.veredito === 'na_media' ? t('trafficManager.expected.headOn', 'Dentro da média')
      : e.veredito === 'pouco_volume' ? t('trafficManager.expected.headLow', 'Pouco volume para comparar') : t('trafficManager.expected.headNoBase', 'Sem média de referência');
  const explica = e.veredito === 'sem_base'
    ? t('trafficManager.expected.noBaseHelp', 'Os 90 dias antes do mês têm menos de 5 conversões nestas campanhas: não há média confiável para comparar.')
    : t('trafficManager.expected.explain', '{{r}} conversões no mês contra {{x}} que a média entregaria com o mesmo gasto ({{g}}).', { r: f.dec(e.real.conversions, 1), x: f.dec(e.esperado ?? 0, 1), g: money(e.real.spend) })
      + ' ' + (e.veredito === 'na_media' ? t('trafficManager.expected.withinChance', 'A diferença ainda cabe na variação normal (±{{m}}).', { m: f.dec(e.margem ?? 0, 1) })
        : e.veredito === 'pouco_volume' ? t('trafficManager.expected.lowHelp', 'Com tão poucas conversões, qualquer diferença pode ser acaso.')
          : t('trafficManager.expected.beyondChance', 'A diferença passa do que o acaso explicaria (±{{m}}).', { m: f.dec(e.margem ?? 0, 1) }));
  return (
    <Card>
      <SectionTitle icon={Scale}>{t('trafficManager.expected.title', 'Esperado x real no mês')}</SectionTitle>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 -mt-1 mb-4">
        <div className="text-2xl font-bold text-gray-900 dark:text-white">{titulo}</div>
        {(e.veredito === 'acima' || e.veredito === 'abaixo') && <VerdictPill v={e.veredito} />}
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{explica}</p>
      <ExpectedChart data={e.pontos} />
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Comparado label={t('trafficManager.metrics.cpa', 'Custo/conv.')} real={e.real.cpa} base={e.base.cpa} fmt={money} lowerIsBetter />
        <Comparado label={t('trafficManager.metrics.convRate', 'Taxa de conversão')} real={e.real.conv_rate} base={e.base.conv_rate} fmt={pct} />
        <Comparado label={t('trafficManager.metrics.cpc', 'CPC')} real={e.real.cpc} base={e.base.cpc} fmt={money} lowerIsBetter />
        <Comparado label={t('trafficManager.metrics.ctr', 'CTR')} real={e.real.ctr} base={e.base.ctr} fmt={pct} />
        <div className="col-span-2 lg:col-span-1 min-w-0">
          <div className="text-xs text-gray-500">{t('trafficManager.expected.projectionEnd', 'Projeção até o fim do mês')}</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">{e.projecao_fim_do_mes != null ? f.dec(e.projecao_fim_do_mes, 0) : '—'}</div>
          <div className="text-xs text-gray-500">
            {e.esperado_com_a_verba != null && t('trafficManager.expected.withBudget', 'média com a verba: {{n}}', { n: f.dec(e.esperado_com_a_verba, 0) })}
            {e.meta_com_a_verba != null && <> · {t('trafficManager.expected.withTarget', 'meta: {{n}}', { n: f.dec(e.meta_com_a_verba, 0) })}</>}
          </div>
        </div>
      </div>
      {camp.length > 0 && (
        <div className="mt-5">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('trafficManager.expected.byCampaign', 'Cada campanha contra a própria média')}</h3>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {visiveis.map((c) => (
              <li key={c.id} className="py-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                <span className="text-sm text-gray-900 dark:text-white truncate sm:flex-1 min-w-0">{c.nome}</span>
                <span className="text-xs text-gray-500 tabular-nums">
                  {t('trafficManager.expected.campLine', '{{r}} conv. · esperado {{x}} · {{c}} (média {{m}})', { r: f.dec(c.conversoes, 1), x: c.esperado != null ? f.dec(c.esperado, 1) : '—', c: money(c.cpa), m: money(c.cpa_media) })}
                </span>
                <VerdictPill v={c.veredito} indice={c.indice} />
              </li>
            ))}
          </ul>
          {camp.length > 6 && (
            <button type="button" onClick={() => setTodas((v) => !v)} className="mt-1 inline-flex items-center gap-1 text-sm text-primary-600 hover:underline">
              {todas ? <><ChevronUp className="w-4 h-4" />{t('trafficManager.overview.showLess', 'Mostrar menos')}</> : <><ChevronDown className="w-4 h-4" />{t('trafficManager.overview.showAll', 'Ver todas ({{n}})', { n: camp.length })}</>}
            </button>
          )}
        </div>
      )}
      <p className="mt-4 text-[11px] text-gray-500">{t('trafficManager.expected.note', 'Média de referência: as mesmas campanhas nos {{d}} dias antes do mês ({{de}} a {{ate}}). Esperado = o que essa média entregaria com o gasto real. Só dias fechados.', { d: e.base.dias, de: f.day(e.base.de), ate: f.day(e.base.ate) })}</p>
    </Card>
  );
};

export default ExpectedCard;
